import type { HttpTypes } from '@medusajs/types';
import {
  CustomerAddressInput,
  MedusaCustomer,
  MedusaCustomerAddress,
  SessionUser,
  SessionUserAddress,
} from '@/common/interfaces/medusa.interface';

const DEFAULT_COUNTRY = 'India';
const DEFAULT_COUNTRY_CODE = 'in';

function countryName(countryCode: string | null | undefined): string {
  if (!countryCode) {
    return '';
  }

  return countryCode.toLowerCase() === DEFAULT_COUNTRY_CODE
    ? DEFAULT_COUNTRY
    : countryCode.toUpperCase();
}

function countryCode(country: string | null | undefined): string {
  if (!country) {
    return DEFAULT_COUNTRY_CODE;
  }

  return country.trim().toLowerCase() === DEFAULT_COUNTRY.toLowerCase()
    ? DEFAULT_COUNTRY_CODE
    : country.trim().toLowerCase().slice(0, 2);
}

function landmarkFromMetadata(
  metadata: Record<string, unknown> | null,
): string | null {
  const landmark = metadata?.['landmark'];
  return typeof landmark === 'string' && landmark.trim() ? landmark : null;
}

export function toSessionUserAddress(
  address: MedusaCustomerAddress,
): SessionUserAddress {
  return {
    id: address.id,
    label: address.address_name,
    firstName: address.first_name ?? '',
    lastName: address.last_name ?? '',
    phone: address.phone ?? '',
    line1: address.address_1 ?? '',
    line2: address.address_2,
    city: address.city ?? '',
    state: address.province ?? '',
    postalCode: address.postal_code ?? '',
    country: countryName(address.country_code),
    landmark: landmarkFromMetadata(address.metadata),
    isDefaultShipping: address.is_default_shipping,
    isDefaultBilling: address.is_default_billing,
  };
}

export function toSessionUser(customer: MedusaCustomer): SessionUser {
  const addresses = (customer.addresses ?? []).map(toSessionUserAddress);

  return {
    id: customer.id,
    email: customer.email,
    isActive: !customer.deleted_at,
    role: null,
    profile: {
      id: customer.id,
      firstName: customer.first_name ?? '',
      lastName: customer.last_name ?? '',
      phone: customer.phone ?? null,
      avatarUrl: null,
    },
    preference: {
      id: customer.id,
      defaultShippingAddressId: customer.default_shipping_address_id ?? null,
      defaultBillingAddressId: customer.default_billing_address_id ?? null,
    },
    notificationPreferences: [],
    addresses,
  };
}

export function toAddressPayload(
  input: CustomerAddressInput,
): HttpTypes.StoreCreateCustomerAddress {
  return {
    address_name: input.label?.trim() || null,
    first_name: input.firstName.trim(),
    last_name: input.lastName.trim(),
    phone: input.phone.trim(),
    address_1: input.line1.trim(),
    address_2: input.line2?.trim() || null,
    city: input.city.trim(),
    province: input.state.trim() || null,
    postal_code: input.postalCode.trim(),
    country_code: countryCode(input.country),
    metadata: input.landmark?.trim()
      ? { landmark: input.landmark.trim() }
      : null,
  };
}
